import { supabase } from "@/integrations/supabase/client";

export interface GeoIpData {
  ip: string;
  country_code: string | null;
  city: string | null;
  isp: string | null;
}

/**
 * Fetch the user's IP address and geo-location data using ip-api.com.
 * Falls back to ipapi.co (HTTPS) if ip-api.com fails, then to ipify as last resort.
 */
export async function getGeoIpData(): Promise<GeoIpData | null> {
  try {
    const response = await fetch(
      "http://ip-api.com/json/?fields=query,countryCode,city,isp",
      {
        signal: AbortSignal.timeout(5000),
      },
    );
    if (response.ok) {
      const data = await response.json();
      if (data.query) {
        return {
          ip: data.query,
          country_code: data.countryCode || null,
          city: data.city || null,
          isp: data.isp || null,
        };
      }
    }
  } catch (e) {
    console.warn("[GEO_IP] ip-api.com failed, trying fallback:", e);
  }

  try {
    const response = await fetch("https://ipapi.co/json/", {
      signal: AbortSignal.timeout(5000),
    });
    if (response.ok) {
      const data = await response.json();
      if (data.ip) {
        return {
          ip: data.ip,
          country_code: data.country_code || null,
          city: data.city || null,
          isp: data.org || null,
        };
      }
    }
  } catch (e) {
    console.warn("[GEO_IP] ipapi.co failed, trying last resort:", e);
  }

  try {
    const response = await fetch("https://api.ipify.org?format=json", {
      signal: AbortSignal.timeout(5000),
    });
    if (response.ok) {
      const data = await response.json();
      if (data.ip) {
        return { ip: data.ip, country_code: null, city: null, isp: null };
      }
    }
  } catch (e) {
    console.warn("[GEO_IP] All IP services failed:", e);
  }

  return null;
}

/**
 * Get just the IP address string (backwards-compatible helper for fraud prevention)
 */
export async function getUserIPAddress(): Promise<string | null> {
  const geoData = await getGeoIpData();
  return geoData?.ip || null;
}

/**
 * Upsert geo-location data into stream_ip_analytics after a stream is recorded.
 * Table has unique constraint on ip_address only (no user_id column).
 */
export async function upsertStreamGeoData(
  userId: string,
  geoData: GeoIpData,
): Promise<void> {
  try {
    const query = supabase.from("stream_ip_analytics") as any;

    // Check if record exists for this IP
    const { data: existing } = await query
      .select("id, total_streams")
      .eq("ip_address", geoData.ip)
      .maybeSingle();

    if (existing) {
      await (supabase.from("stream_ip_analytics") as any)
        .update({
          country_code: geoData.country_code,
          city: geoData.city,
          isp: geoData.isp,
          total_streams: (existing.total_streams || 0) + 1,
          last_activity_at: new Date().toISOString(),
        })
        .eq("id", existing.id);
    } else {
      await (supabase.from("stream_ip_analytics") as any).insert({
        ip_address: geoData.ip,
        country_code: geoData.country_code,
        city: geoData.city,
        isp: geoData.isp,
        total_streams: 1,
        first_seen_at: new Date().toISOString(),
        last_activity_at: new Date().toISOString(),
      });
    }

    console.log("📍 [GEO_IP] Stream geo data upserted:", {
      ip: geoData.ip,
      country_code: geoData.country_code,
      city: geoData.city,
    });
  } catch (error) {
    // Non-blocking — don't fail the stream if geo tracking fails
    console.error("⚠️ [GEO_IP] Failed to upsert geo data:", error);
  }
}
